import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldCheck, AlertTriangle, Info, DollarSign, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { ragComplianceFindingService, documentChunkService, profileService } from '@/services';
import type { RagComplianceFinding } from '@/services';

interface AdvancedComplianceTabProps {
  invoiceId: string;
  priceBookId: string | null;
}

const severityConfig: Record<string, { label: string; color: string; icon: typeof AlertTriangle }> = {
  high: { label: 'High', color: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800', icon: AlertTriangle },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800', icon: AlertTriangle },
  low: { label: 'Low', color: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800', icon: Info },
  info: { label: 'Info', color: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-800', icon: Info },
};

const findingTypeLabels: Record<string, string> = {
  overcharge: 'Overcharge',
  missing_surcharge: 'Missing Surcharge',
  terms_violation: 'Terms Violation',
  minimum_not_met: 'Minimum Not Met',
  conditional_rule: 'Conditional Rule',
  pricing_tier_error: 'Pricing Tier Error',
  info: 'Information',
};

export default function AdvancedComplianceTab({ invoiceId, priceBookId }: AdvancedComplianceTabProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const result = await profileService.get(user!.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Check if price book has vectorized documents
  const { data: hasVectorized } = useQuery({
    queryKey: ['pricebook-vectorized', priceBookId],
    queryFn: async () => {
      const result = await documentChunkService.hasVectorizedDocuments('price_book', priceBookId!);
      if (result.error) return false;
      return result.hasChunks;
    },
    enabled: !!priceBookId,
  });

  // Fetch existing findings
  const { data: findings = [], isLoading: isLoadingFindings } = useQuery({
    queryKey: ['rag-compliance-findings', invoiceId],
    queryFn: async () => {
      const result = await ragComplianceFindingService.listByInvoice(invoiceId);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!invoiceId,
  });

  // Run compliance check mutation
  const runCheck = useMutation({
    mutationFn: async () => {
      if (!profile?.company_id) throw new Error('Missing profile');
      const result = await ragComplianceFindingService.runForInvoice(
        invoiceId,
        { companyId: profile.company_id, userId: user?.id }
      );
      if (result.error) throw result.error;
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rag-compliance-findings', invoiceId] });
      toast({ title: 'Advanced compliance check complete', description: 'Results have been saved.' });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const canRun = !!priceBookId && hasVectorized && !runCheck.isPending;

  // Summary stats
  const totalFindings = findings.length;
  const highCount = findings.filter(f => f.severity === 'high').length;
  const mediumCount = findings.filter(f => f.severity === 'medium').length;
  const lowCount = findings.filter(f => f.severity === 'low').length;
  const infoCount = findings.filter(f => f.severity === 'info').length;
  const totalImpact = findings.reduce((sum, f) => sum + (f.estimated_impact || 0), 0);

  const lastRunTime = findings.length > 0 ? findings[0].created : null;
  const lastProvider = findings.length > 0 ? findings[0].ai_provider : null;
  const lastModel = findings.length > 0 ? findings[0].ai_model : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          onClick={() => runCheck.mutate()}
          disabled={!canRun}
        >
          {runCheck.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <ShieldCheck className="w-4 h-4 mr-2" />
          )}
          {runCheck.isPending ? 'Analyzing...' : 'Run Advanced Compliance'}
        </Button>

        {lastRunTime && (
          <div className="text-xs text-muted-foreground">
            Last run: {new Date(lastRunTime).toLocaleString()}
          </div>
        )}
      </div>

      {/* Warnings */}
      {!priceBookId && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm text-yellow-800 dark:text-yellow-400">
          This invoice has no linked price book. A price book is required for advanced compliance checks.
        </div>
      )}
      {priceBookId && hasVectorized === false && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm text-yellow-800 dark:text-yellow-400">
          The linked price book has no vectorized documents. Upload and vectorize a price book PDF in the Price Book detail page first.
        </div>
      )}

      {/* Summary Bar */}
      {totalFindings > 0 && (
        <div className="flex items-center gap-4 p-3 bg-card border border-border rounded-lg">
          <span className="text-sm font-medium">{totalFindings} finding{totalFindings !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-2">
            {highCount > 0 && <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">{highCount} High</Badge>}
            {mediumCount > 0 && <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">{mediumCount} Medium</Badge>}
            {lowCount > 0 && <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">{lowCount} Low</Badge>}
            {infoCount > 0 && <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">{infoCount} Info</Badge>}
          </div>
          {totalImpact > 0 && (
            <div className="ml-auto flex items-center gap-1 text-sm font-medium">
              <DollarSign className="w-4 h-4 text-muted-foreground" />
              Est. Impact: ${totalImpact.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {isLoadingFindings && (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* Empty State */}
      {!isLoadingFindings && findings.length === 0 && !runCheck.isPending && (
        <div className="p-8 text-center text-muted-foreground bg-card border border-border rounded-lg">
          {lastRunTime
            ? 'No advanced compliance issues found.'
            : 'Run an advanced compliance check to identify contractual rule violations.'
          }
        </div>
      )}

      {/* Running State */}
      {runCheck.isPending && (
        <div className="p-8 text-center bg-card border border-border rounded-lg">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Analyzing invoice against price book contractual rules...
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            This may take 30-60 seconds.
          </p>
        </div>
      )}

      {/* Findings List */}
      {!isLoadingFindings && findings.length > 0 && (
        <div className="space-y-3">
          {findings.map((finding) => (
            <FindingCard key={finding.id} finding={finding} />
          ))}
        </div>
      )}

      {lastProvider && lastModel && (
        <div className="text-[10px] text-muted-foreground/30 text-right select-none">
          via {lastProvider}/{lastModel}
        </div>
      )}
    </div>
  );
}

function FindingCard({ finding }: { finding: RagComplianceFinding }) {
  const severity = severityConfig[finding.severity] || severityConfig.info;
  const SeverityIcon = severity.icon;

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Badge className={severity.color}>
            <SeverityIcon className="w-3 h-3 mr-1" />
            {severity.label}
          </Badge>
          <Badge variant="outline">
            {findingTypeLabels[finding.finding_type] || finding.finding_type}
          </Badge>
        </div>
        {finding.estimated_impact != null && finding.estimated_impact > 0 && (
          <span className="text-sm font-medium text-red-600 dark:text-red-400">
            ${finding.estimated_impact.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        )}
      </div>

      <h4 className="text-sm font-semibold mb-1">{finding.title}</h4>
      <p className="text-sm text-muted-foreground mb-3">{finding.description}</p>

      {/* Citations */}
      {finding.source_citations && finding.source_citations.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            <Quote className="w-3 h-3" />
            Source Citations
          </div>
          {finding.source_citations.map((citation, i) => (
            <div
              key={i}
              className="text-xs text-muted-foreground bg-muted/50 rounded px-3 py-2 border-l-2 border-primary/30 italic"
            >
              {citation}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
